import { PDFDocument } from "pdf-lib";

/** One attachment's bytes, resolved from a staged file or a stored document. */
export interface ResolvedAttachment {
  name: string;
  bytes: Uint8Array;
}

export interface AppendResult {
  bytes: Uint8Array;
  /** Names whose pages were appended. */
  appended: string[];
  /** Names the PDF can only reference, not carry — listed for the caller to
   *  report rather than dropped silently. */
  notAppended: string[];
}

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 24;

function magic(bytes: Uint8Array, signature: number[]): boolean {
  return signature.every((byte, index) => bytes[index] === byte);
}

/** Sniffed from the bytes, not the extension — a mislabelled file would
 *  otherwise take the whole merge down on an attachment nobody looked at. */
function kindOf(bytes: Uint8Array): "pdf" | "jpg" | "png" | null {
  if (magic(bytes, [0x25, 0x50, 0x44, 0x46])) return "pdf"; // %PDF
  if (magic(bytes, [0xff, 0xd8, 0xff])) return "jpg";
  if (magic(bytes, [0x89, 0x50, 0x4e, 0x47])) return "png";
  return null;
}

/** Scales to fit the page while keeping the aspect ratio — never enlarges past
 *  the printable area, so a phone photo lands as one readable page. */
function fitted(width: number, height: number) {
  const scale = Math.min((PAGE_WIDTH - MARGIN * 2) / width, (PAGE_HEIGHT - MARGIN * 2) / height, 1);
  const drawn = { width: width * scale, height: height * scale };
  return {
    ...drawn,
    x: (PAGE_WIDTH - drawn.width) / 2,
    y: (PAGE_HEIGHT - drawn.height) / 2,
  };
}

async function appendOne(doc: PDFDocument, attachment: ResolvedAttachment): Promise<boolean> {
  const kind = kindOf(attachment.bytes);
  if (kind === null) return false;

  if (kind === "pdf") {
    // Encrypted or malformed PDFs throw here; they count as not appended
    // rather than failing the save of an otherwise good document.
    const source = await PDFDocument.load(attachment.bytes, { ignoreEncryption: false });
    const pages = await doc.copyPages(source, source.getPageIndices());
    for (const page of pages) doc.addPage(page);
    return true;
  }

  const image =
    kind === "jpg" ? await doc.embedJpg(attachment.bytes) : await doc.embedPng(attachment.bytes);
  doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]).drawImage(image, fitted(image.width, image.height));
  return true;
}

/**
 * Appends what the attachments actually are onto the end of the generated form,
 * so "Add attachments to the PDF" puts the photos and drawings *in* the
 * document rather than only naming them.
 *
 * PDFs contribute their pages; JPEG and PNG each become one fitted page.
 * Anything else — a DWG, a spreadsheet — cannot be drawn on a PDF page, so it
 * stays referenced by name and is reported back in `notAppended`; the caller
 * says so rather than letting the tick box imply otherwise.
 */
export async function appendAttachmentsToPdf(
  formBytes: Uint8Array,
  attachments: ResolvedAttachment[]
): Promise<AppendResult> {
  if (attachments.length === 0) {
    return { bytes: formBytes, appended: [], notAppended: [] };
  }

  const doc = await PDFDocument.load(formBytes);
  const appended: string[] = [];
  const notAppended: string[] = [];

  for (const attachment of attachments) {
    try {
      if (await appendOne(doc, attachment)) appended.push(attachment.name);
      else notAppended.push(attachment.name);
    } catch {
      notAppended.push(attachment.name);
    }
  }

  return { bytes: await doc.save(), appended, notAppended };
}
